import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import './Sidebar.css';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { logout as logoutAction } from '../redux/authSlice';
import { logout as logoutRequest } from '../services/authService';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshToken } = useAppSelector((state) => state.auth);

  const displayName = useMemo(() => {
    if (!user) {
      return '访客';
    }
    return user.name || user.username || '匿名用户';
  }, [user]);

  const initials = useMemo(() => {
    const source = user?.name || user?.username || 'G';
    return source.slice(0, 1).toUpperCase();
  }, [user]);

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleLogout = async () => {
    try {
      if (refreshToken) {
        await logoutRequest({ refresh_token: refreshToken });
      }
    } catch (error) {
      console.warn('退出登录请求失败，忽略继续退出', error);
    } finally {
      dispatch(logoutAction());
      navigate('/login');
    }
  };

  const communityActive = React.useMemo(() => {
    const pathname = location.pathname;
    return pathname.startsWith('/app/community') || pathname.startsWith('/app/community-landing');
  }, [location.pathname]);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {!isCollapsed ? (
            <div className="sidebar-user">
              <span className="sidebar-user-avatar" aria-hidden>
                {initials}
              </span>
              <div>
                <span className="sidebar-user-name">{displayName}</span>
                <span className="sidebar-user-sub">AI 中医诊疗助手</span>
              </div>
            </div>
          ) : (
            <span>导航栏</span>
          )}
        </div>
        <button className="toggle-btn" onClick={toggleSidebar}>
          {isCollapsed ? '▶' : '◀'}
        </button>
      </div>
      <nav className="sidebar-nav">
        <ul>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/chat"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">💬</span>
              {!isCollapsed && <span className="nav-text">AI中医</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/realtime-consult"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">🎧</span>
              {!isCollapsed && <span className="nav-text">实时问诊</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/dietary-recipes"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">🥣</span>
              {!isCollapsed && <span className="nav-text">药膳方</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/herb-library"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">🌿</span>
              {!isCollapsed && <span className="nav-text">药材库</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/face-diagnosis"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">👁️</span>
              {!isCollapsed && <span className="nav-text">影像采集</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/community-landing"
              className={() => (communityActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">🧑‍🤝‍🧑</span>
              {!isCollapsed && <span className="nav-text">中医社区</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/health-calendar"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">📅</span>
              {!isCollapsed && <span className="nav-text">养生日历</span>}
            </NavLink>
          </motion.li>
          <motion.li className="nav-item" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <NavLink
              to="/app/user-profile"
              className={({ isActive }) => (isActive ? 'nav-item active' : 'nav-item')}
            >
              <span className="nav-icon">�</span>
              {!isCollapsed && <span className="nav-text">用户资料</span>}
            </NavLink>
          </motion.li>
        </ul>
      </nav>
      <div className="sidebar-footer">
        <button
          type="button"
          className="sidebar-logout"
          onClick={handleLogout}
        >
          {!isCollapsed && <span className="sidebar-logout-text">退出登录</span>}
          <span aria-hidden>↩</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
